/**
 * PackageRunView — patient's guided pass through a form package.
 *
 * A "package" bundles several forms into one link. The patient completes them
 * one at a time (Step i of N); each step produces its own submission via
 * POST /api/forms/submit with `packageRunItemId`. Resume works by construction:
 * the server returns `nextItemId` (the first not-yet-completed step), so a
 * reopened link always lands on the right form.
 *
 * Two origins share this component:
 *   - Assigned runs → authenticated as a CONTACT (contact JWT in memory). The
 *     Form.io token store is seeded so file uploads carry x-jwt-token.
 *   - Public runs   → authenticated with a role-less run GRANT (passed via the
 *     `grant` prop). Submits carry `runGrant`; the contact-JWT token store is
 *     NOT seeded (there is no contact JWT).
 *
 * Each step honours the member form's ai_mode (served by the package item
 * endpoint): 'form_only' renders the manual Form.io view, while 'ai_only' and
 * 'toggle' open the AI intake chat (toggle also exposes a Chat/Manual switch).
 * The AI chat only COLLECTS — completion forwards the merged data to the same
 * forms/submit path as manual, carrying the contact JWT (assigned) or run grant
 * (public); /ai/intake/complete never inserts for package sessions.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  packageRuns, forms as formsApi, getAuthToken,
  PackageRunDetail, RunItemDetail,
} from '../../lib/api.js';
import { computeRunProgress } from '../../lib/package-run-progress.js';
import { resolveStepMode, stepAllowsToggle, StepMode } from '../../lib/package-step-mode.js';
import { isSessionExpiredError } from '../../lib/grant-session.js';
import { flattenFormFields } from '../../lib/form-fields.js';
import { wireFormForUpload, setFormioToken, clearFormioToken } from '../../lib/formio-file-wiring.js';
import { scrollToFirstFormError, enableSubmitValidation } from '../../lib/formio-ux.js';
import AIIntakeChat from '../../components/chat/AIIntakeChat.js';
import { collectPrefillData } from '@clearintake/shared/identity';

interface Props {
  runId: string;
  /** Public runs pass a run grant; assigned runs rely on the in-memory contact JWT. */
  grant?: string;
  /**
   * Public-run only: called when a load/submit fails because the run grant (or
   * the run itself) has expired, so the host page can bounce back to re-verify.
   * Assigned runs (no grant) never pass this.
   */
  onSessionExpired?: () => void;
}

type FormComponent = React.ComponentType<{
  form: unknown;
  submission?: { data: Record<string, unknown> };
  onSubmit: (submission: { data: Record<string, unknown> }) => void;
  onError?: (errors: unknown) => void;
  options?: Record<string, unknown>;
}>;

export default function PackageRunView({ runId, grant, onSessionExpired }: Props): React.ReactElement {
  const [run,        setRun]        = useState<PackageRunDetail | null>(null);
  const [item,       setItem]       = useState<RunItemDetail | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [savedNote,  setSavedNote]  = useState<string | null>(null);
  const [FormComp,   setFormComp]   = useState<FormComponent | null>(null);
  // Render mode for the CURRENT step (AI chat vs manual form) + the fields the AI
  // collected so far, seeded into the manual fallback when a toggle step switches.
  const [mode,       setMode]       = useState<StepMode>('manual');
  const [seededData, setSeededData] = useState<Record<string, unknown>>({});

  // Lazy-load @formio/react (matches AssignedFormView).
  useEffect(() => {
    import('@formio/react').then((mod) => {
      setFormComp(() => (mod as { Form: FormComponent }).Form);
    }).catch(() => { /* no formio */ });
  }, []);

  // Load (or reload) the run + its current step. The server's nextItemId is the
  // authoritative resume point.
  const loadRun = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await packageRuns.get(runId, grant);
    if (!res.success || !res.data) {
      if (grant && onSessionExpired && isSessionExpiredError(res.error)) { onSessionExpired(); return; }
      setError(res.error ?? 'This package could not be loaded.');
      setLoading(false);
      return;
    }
    setRun(res.data);
    const nextId = res.data.nextItemId;
    if (!nextId) {
      // All steps complete.
      setItem(null);
      setLoading(false);
      return;
    }
    const itemRes = await packageRuns.getItem(runId, nextId, grant);
    if (itemRes.success && itemRes.data) {
      // Open the step in the mode its form dictates (set alongside the item so the
      // correct renderer paints on the first frame); drop any carried-over seed.
      setItem(itemRes.data);
      setMode(resolveStepMode(itemRes.data.aiMode));
      setSeededData({});
    } else if (grant && onSessionExpired && isSessionExpiredError(itemRes.error)) {
      onSessionExpired();
      return;
    } else {
      setError(itemRes.error ?? 'This form step could not be loaded.');
    }
    setLoading(false);
  }, [runId, grant, onSessionExpired]);

  useEffect(() => { loadRun(); }, [loadRun]);

  const handleSubmit = useCallback(async (submission: { data: Record<string, unknown> }) => {
    if (!item) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await formsApi.submit({
        formId:           item.formId,
        packageRunItemId: item.itemId,
        locationId:       item.locationId,
        data:             submission.data,
        ...(grant ? { runGrant: grant } : {}),
      });
      if (res.success) {
        setSavedNote(`✓ ${item.formName} saved`);
        setItem(null);
        await loadRun();
        window.setTimeout(() => setSavedNote(null), 1600);
      } else if (grant && onSessionExpired && isSessionExpiredError(res.error)) {
        onSessionExpired();
      } else {
        setError(res.error ?? 'Submission failed');
      }
    } catch (err) {
      setError(`Submission error: ${(err as Error).message ?? 'Network error — check your connection'}`);
    } finally {
      setSubmitting(false);
    }
  }, [item, grant, loadRun, onSessionExpired]);

  const progress = useMemo(() => computeRunProgress(run?.items ?? []), [run]);

  // Completion screen (run has no remaining steps).
  if (run && !loading && !item && progress.allDone) {
    return (
      <div className="ci-portal" style={styles.thankYou}>
        <div style={styles.thankYouIcon}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--ci-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h2 style={styles.tyTitle}>All {progress.total} forms completed</h2>
        <p style={styles.tySub}>Your intake has been submitted securely. Your care team has been notified and will review your information before your appointment.</p>
      </div>
    );
  }

  if (loading) return <div style={styles.center}><p style={styles.muted}>Loading…</p></div>;
  if (error && !item) return <div className="ci-portal" style={styles.center}><p style={{ color: '#c0392b', fontFamily: F }}>{error}</p></div>;
  if (!run || !item) return <div className="ci-portal" style={styles.center}><p style={styles.muted}>Nothing to complete.</p></div>;

  return (
    <div className="ci-portal" style={styles.page}>
      {/* Secure banner */}
      <div style={styles.secureBanner}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        Secure &amp; Encrypted Forms
      </div>

      <div style={styles.container}>
        {/* Package header + progress */}
        <div style={styles.headerCard}>
          <h1 style={styles.pkgTitle}>{run.packageName}</h1>
          {run.description && <p style={styles.pkgDesc}>{run.description}</p>}
          <p style={styles.stepLabel}>Step {progress.stepNumber} of {progress.total}</p>
          <ol style={styles.progressList}>
            {progress.ordered.map((it) => {
              const isCurrent = it.itemId === item.itemId;
              const isDone = it.completedAt !== null;
              return (
                <li key={it.itemId} style={{ ...styles.progressItem, ...(isCurrent ? styles.progressItemCurrent : {}) }}>
                  <span style={styles.progressMark} aria-hidden="true">{isDone ? '✓' : (isCurrent ? '•' : '')}</span>
                  <span style={{ color: isDone ? 'var(--ci-text-muted)' : 'var(--ci-text)' }}>{it.formName}</span>
                </li>
              );
            })}
          </ol>
        </div>

        {savedNote && <div style={styles.savedNote} role="status">{savedNote}</div>}

        <h2 style={styles.formTitle}>{item.formName}</h2>
        {item.description && <p style={styles.formDesc}>{item.description}</p>}

        {/* Chat/Manual switch — toggle steps only */}
        {stepAllowsToggle(item.aiMode) && (
          <div style={styles.modeToggle}>
            <button
              className="ci-mode-btn"
              data-active={mode === 'ai'}
              style={{ ...styles.modeBtn, ...(mode === 'ai' ? styles.modeBtnActive : {}) }}
              onClick={() => setMode('ai')}
            >
              Chat with AI
            </button>
            <button
              className="ci-mode-btn"
              data-active={mode === 'manual'}
              style={{ ...styles.modeBtn, ...(mode === 'manual' ? styles.modeBtnActive : {}) }}
              onClick={() => setMode('manual')}
            >
              Fill form myself
            </button>
          </div>
        )}

        {error && <div style={styles.errorBox} role="alert">{error}</div>}

        {(mode === 'ai' && item.aiMode !== 'form_only') ? (
          <AIIntakeChat
            key={item.itemId}
            formId={item.formId}
            locationId={item.locationId}
            packageRunItemId={item.itemId}
            // Assigned runs authenticate with the in-memory contact JWT; public
            // runs carry the run grant instead (no JWT).
            authToken={grant ? undefined : (getAuthToken() ?? undefined)}
            runGrant={grant}
            tenantName={item.formName}
            formFields={flattenFormFields(
              (item.schemaJson as { components?: Parameters<typeof flattenFormFields>[0] } | undefined)?.components,
            )}
            onSubmit={(data) => { void handleSubmit({ data }); }}
            onSwitchToForm={stepAllowsToggle(item.aiMode)
              ? (collected) => { setSeededData(collected); setMode('manual'); }
              : undefined}
          />
        ) : (
          <ManualStep
            key={item.itemId}
            schemaJson={item.schemaJson}
            formId={item.formId}
            locationId={item.locationId}
            FormComp={FormComp}
            onSubmit={handleSubmit}
            submitting={submitting}
            seedToken={!grant}
            initialData={seededData}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One package step — manual Form.io render (mirrors AssignedFormView.ManualFormView)
// ---------------------------------------------------------------------------

function ManualStep({
  schemaJson, formId, locationId, FormComp, onSubmit, submitting, seedToken, initialData,
}: {
  schemaJson: unknown;
  formId: string;
  locationId: string;
  FormComp: FormComponent | null;
  onSubmit: (submission: { data: Record<string, unknown> }) => void;
  submitting: boolean;
  /** Seed the Form.io token store with the contact JWT (assigned runs only). */
  seedToken: boolean;
  /** Values carried over when a toggle step switches from AI → manual (fallback inputs). */
  initialData?: Record<string, unknown>;
}): React.ReactElement {
  const [formData, setFormData] = useState<Record<string, unknown>>(initialData ?? {});

  const wiredSchema = useMemo(
    () => (schemaJson ? enableSubmitValidation(wireFormForUpload(schemaJson, formId, locationId)) : schemaJson),
    [schemaJson, formId, locationId],
  );
  // Route date-family prefill (DOB) through Form.io submission data — datetime widgets
  // don't paint a schema defaultValue on load. Stable ref so re-renders don't reset input.
  const prefill = useMemo(() => ({ data: collectPrefillData(wiredSchema) }), [wiredSchema]);

  // Only assigned runs have a contact JWT; public runs authenticate uploads via
  // the run grant (handled by the public page), so we don't seed the token store.
  useEffect(() => {
    if (!seedToken) return undefined;
    setFormioToken();
    return () => clearFormioToken();
  }, [seedToken]);

  if (FormComp && schemaJson) {
    return (
      <div className="ci-formio-form" style={styles.formioWrap}>
        <FormComp
          form={wiredSchema}
          submission={prefill}
          onSubmit={onSubmit}
          onError={() => scrollToFirstFormError()}
          options={{}}
        />
        {submitting && <p style={styles.muted}>Submitting…</p>}
      </div>
    );
  }

  // Fallback: dynamic text inputs from schema components.
  const components = ((schemaJson as { components?: { key: string; label: string; type: string }[] })?.components ?? []);
  return (
    <div style={styles.fallbackForm}>
      {components.map((c) => (
        <div key={c.key} style={styles.fallbackField}>
          <label style={styles.fallbackLabel}>{c.label}</label>
          <input
            className="ci-focusable"
            style={styles.input}
            value={String(formData[c.key] ?? '')}
            onChange={(e) => setFormData({ ...formData, [c.key]: e.target.value })}
          />
        </div>
      ))}
      <button
        style={{ ...styles.submitBtn, opacity: submitting ? 0.7 : 1 }}
        onClick={() => onSubmit({ data: formData })}
        disabled={submitting}
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>
    </div>
  );
}

const F = 'var(--ci-font)';

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight:  '100vh',
    background: 'var(--ci-surface)',
    fontFamily: F,
  },
  secureBanner: {
    background:     '#1a2744',
    color:          'rgba(255,255,255,0.82)',
    padding:        '9px 20px',
    fontSize:       12,
    textAlign:      'center',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            6,
    letterSpacing:  '0.01em',
    fontFamily:     F,
  },
  container:   { maxWidth: 960, margin: '0 auto', padding: '24px 24px 48px' },
  headerCard: {
    background:   '#ffffff',
    borderRadius: 16,
    padding:      '20px 24px',
    border:       '1px solid var(--ci-border)',
    marginBottom: 20,
  },
  pkgTitle:  { fontSize: 20, fontWeight: 700, color: 'var(--ci-text)', margin: '0 0 6px', fontFamily: F },
  pkgDesc:   { fontSize: 14, color: 'var(--ci-text-muted)', lineHeight: 1.6, margin: '0 0 12px', fontFamily: F },
  stepLabel: { fontSize: 13, fontWeight: 600, color: 'var(--ci-primary)', margin: '0 0 12px', fontFamily: F },
  progressList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  progressItem: {
    display:     'flex',
    alignItems:  'center',
    gap:         10,
    fontSize:    14,
    fontFamily:  F,
  },
  progressItemCurrent: { fontWeight: 600 },
  progressMark: {
    width:          18,
    textAlign:      'center',
    color:          'var(--ci-primary)',
    fontWeight:     700,
    flexShrink:     0,
  },
  savedNote: {
    background:   'var(--ci-primary-light)',
    border:       '1px solid var(--ci-border)',
    borderRadius: 8,
    padding:      '10px 14px',
    color:        'var(--ci-primary)',
    fontSize:     14,
    fontWeight:   600,
    marginBottom: 16,
    fontFamily:   F,
  },
  formTitle:   { fontSize: 22, fontWeight: 700, color: 'var(--ci-text)', margin: '0 0 8px', fontFamily: F },
  formDesc:    { fontSize: 15, color: 'var(--ci-text-muted)', lineHeight: 1.65, margin: '0 0 20px', fontFamily: F },
  modeToggle:  { display: 'flex', gap: 8, marginBottom: 20 },
  modeBtn: {
    flex:         1,
    padding:      '12px',
    minHeight:    44,
    border:       '2px solid var(--ci-border)',
    borderRadius: 8,
    background:   '#ffffff',
    cursor:       'pointer',
    fontSize:     14,
    fontWeight:   500,
    color:        'var(--ci-text-body)',
    fontFamily:   F,
    transition:   'border-color 0.15s, color 0.15s, background 0.15s',
  },
  modeBtnActive: {
    borderColor: 'var(--ci-primary)',
    color:       'var(--ci-primary)',
    background:  'var(--ci-primary-light)',
  },
  errorBox: {
    background:   '#fef4f2',
    border:       '1px solid #f5c6bc',
    borderRadius: 8,
    padding:      '10px 14px',
    color:        '#c0392b',
    fontSize:     13,
    marginBottom: 12,
    fontFamily:   F,
  },
  center: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    minHeight:      '100vh',
    background:     'var(--ci-surface)',
  },
  muted: { color: 'var(--ci-text-muted)', fontSize: 14, fontFamily: F },
  thankYou: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    minHeight:      '100vh',
    gap:            16,
    fontFamily:     F,
    background:     'var(--ci-surface)',
  },
  thankYouIcon: {
    width:          72,
    height:         72,
    borderRadius:   '50%',
    background:     'var(--ci-primary-light)',
    border:         '1px solid var(--ci-border)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
  tyTitle: { fontSize: 24, fontWeight: 700, color: 'var(--ci-text)', margin: 0, fontFamily: F, textAlign: 'center' },
  tySub:   { fontSize: 16, color: 'var(--ci-text-muted)', margin: 0, fontFamily: F, textAlign: 'center', maxWidth: 340, lineHeight: 1.6 },
  submitBtn: {
    width:        '100%',
    padding:      '12px',
    minHeight:    46,
    background:   'var(--ci-primary)',
    color:        '#fff',
    border:       'none',
    borderRadius: 8,
    fontSize:     15,
    fontWeight:   600,
    cursor:       'pointer',
    fontFamily:   F,
    letterSpacing: '0.01em',
    transition:   'background 0.15s',
  },
  formioWrap:    { background: '#ffffff', borderRadius: 14, padding: '24px', border: '1px solid var(--ci-border)' },
  fallbackForm:  { background: '#ffffff', borderRadius: 14, padding: '24px', border: '1px solid var(--ci-border)' },
  fallbackField: { marginBottom: 16 },
  fallbackLabel: { display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ci-text-body)', marginBottom: 6, fontFamily: F },
  input: {
    width:        '100%',
    padding:      '11px 12px',
    minHeight:    44,
    border:       '1px solid var(--ci-border)',
    borderRadius: 8,
    fontSize:     15,
    color:        'var(--ci-text)',
    boxSizing:    'border-box' as const,
    fontFamily:   F,
    outline:      'none',
    transition:   'border-color 0.15s',
  },
};
